import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const PendingUsers = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const res = await adminAPI.getPendingUsers();
      if (res.data.success) {
        setUsers(res.data.data || []);
      }
    } catch (error) {
      console.error(error);
      showError('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {

    if (!userId) {
      showError('Error: User ID is missing');
      return;
    }
    try {
      const res = await adminAPI.approveUser(userId);
      if (res.data.success) {
        showSuccess('User approved successfully');
        fetchPendingUsers();
      } else {
        showError(res.data.message || 'Failed to approve');
      }
    } catch (error) {
      console.error('Approve error:', error);
      showError(error.response?.data?.message || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {

    if (!userId) {
      showError('Error: User ID is missing');
      return;
    }
    try {
      const res = await adminAPI.rejectUser(userId);
      if (res.data.success) {
        showSuccess('User rejected');
        fetchPendingUsers();
      } else {
        showError(res.data.message || 'Failed to reject');
      }
    } catch (error) {
      console.error('Reject error:', error);
      showError(error.response?.data?.message || 'Failed to reject user');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-light">Loading pending users...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gold">Pending User Approvals</h1>
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="bg-gray-700 text-light py-2 px-4 rounded-lg hover:bg-gray-600"
        >
          Back to Dashboard
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        {users.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>No pending user approvals</p>
          </div>
        ) : (
          <div className="space-y-4">
            {users.map((user) => (
              <div
                key={user._id}
                className="bg-gray-700 rounded-lg overflow-hidden border border-gray-600"
              >
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-gray-600">
                  <div>
                    <div className="flex items-center gap-3">
                      {user.profile_image ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/uploads/${user.profile_image}`}
                          alt={user.name}
                          className="w-12 h-12 rounded-full object-cover border border-gold"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center text-xs text-gray-400">
                          No Img
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-light">{user.name}</h3>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 ml-15">
                      <p className="text-gray-400 text-xs capitalize">Role: <span className="text-gold font-medium">{user.role}</span></p>
                      <p className="text-gray-400 text-xs">Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(user._id)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(user._id)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                    >
                      Reject
                    </button>
                  </div>
                </div>

                {/* Secure Document Preview Section */}
                {user.documents && user.documents.length > 0 ? (
                  <div className="p-4 bg-gray-800/50">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Verification Documents ({user.documents.length})</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {user.documents.map((doc, idx) => (
                        <div key={idx} className="group relative bg-gray-900 rounded-lg border border-gray-600 overflow-hidden">
                          <div className="aspect-square flex items-center justify-center relative">
                            {doc.file_path.toLowerCase().endsWith('.pdf') ? (
                              <div className="text-center p-2">
                                <svg className="w-8 h-8 text-red-500 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M4 4a2 2 0 012-2h4.586A1 1 0 0111 2.293l4.707 4.707a1 1 0 01.293.707V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                </svg>
                                <span className="text-[10px] text-gray-400 font-mono">PDF</span>
                              </div>
                            ) : (
                              <img
                                src={`${process.env.REACT_APP_API_URL}/admin/documents/${doc.file_path}?token=${localStorage.getItem('token')}`}
                                alt="doc"
                                className="w-full h-full object-cover"
                              />
                            )}

                            {/* Explicit Action Overlay */}
                            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2">
                              <a
                                href={`${process.env.REACT_APP_API_URL}/admin/documents/${doc.file_path}?token=${localStorage.getItem('token')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-gold text-dark px-3 py-1.5 rounded text-xs font-bold hover:bg-yellow-400 transition-colors transform hover:scale-105"
                              >
                                OPEN FILE
                              </a>
                            </div>
                          </div>
                          <div className="p-2 bg-gray-800 border-t border-gray-700">
                            <p className="text-[10px] text-gray-500 truncate text-center" title={doc.document_type}>
                              {doc.document_type || 'Document'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-800/30 text-xs text-gray-500 italic">
                    No documents uploaded for verification yet.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingUsers;
